<script lang="ts" setup>
import { isMobile } from "@/modules/common";
import {
  useScroll,
  useScrollLock,
  useSwipe,
  useThrottleFn,
  useWindowSize,
} from "@vueuse/core";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

const props = defineProps<{
  headerHeight: number;
  isTopNav?: boolean;
  scrollContainerGetter?: () => HTMLElement;
}>();

const enableScrollLock = isMobile();
const headerRef = ref<HTMLElement | null>(null);
const scrollerRef = ref<HTMLElement | null>(null);
const scrollContainerRef = ref<HTMLElement | null>(null);
const actualScroller = computed(() => {
  return scrollContainerRef.value ?? scrollerRef.value;
});
const NAV_BAR_HEIGHT: number = 66;
const GAP_HEIGHT: number = 20;
const { height: windowHeight } = useWindowSize();

const maxPanelHeight = computed(
  () => windowHeight.value - (props.isTopNav ? 0 : NAV_BAR_HEIGHT)
);
const minPanelHeight = computed(() => {
  return (
    windowHeight.value -
    props.headerHeight -
    (props.isTopNav ? 0 : NAV_BAR_HEIGHT) -
    GAP_HEIGHT
  );
});
const route = useRoute();
const panelHeight = ref(minPanelHeight.value);
const enableContentDraggable = ref(false);
const isReachMaxHeight = ref(false);
const isReachMinHeight = ref(true);
const scrollLock = useScrollLock(actualScroller, enableScrollLock);
onMounted(() => {
  scrollerRef.value = document.querySelector(
    ".bottom-sheet-panel .van-floating-panel__content"
  ) as HTMLElement;
  refreshScrollContainer();
});

const refreshScrollContainer = () => {
  if (props.scrollContainerGetter) {
    scrollContainerRef.value = props.scrollContainerGetter();
  }
  const { isSwiping, direction } = useSwipe(actualScroller.value);
  const { arrivedState } = useScroll(actualScroller.value);

  watch(isReachMaxHeight, (isReachMaxHeight) => {
    scrollLock.value = !isReachMaxHeight && enableScrollLock;
  });

  watch(
    [isSwiping, direction],
    ([isSwiping, direction]) => {
      if (!isSwiping) {
        return;
      }

      if (direction === "down" && arrivedState.top) {
        if (isReachMinHeight.value) {
          enableContentDraggable.value = false;
        } else {
          enableContentDraggable.value = true;
        }
      } else if (direction === "up" && isReachMinHeight.value) {
        enableContentDraggable.value = true;
      }
    },
    {
      flush: "pre",
    }
  );
};

const handleHeightChange = ({ height }: { height: number }) => {
  isReachMaxHeight.value = height >= maxPanelHeight.value;
  isReachMinHeight.value = height <= minPanelHeight.value;
  // 若panel高度仍有增长空间，则允许拖拽
  if (height < maxPanelHeight.value) {
    enableContentDraggable.value = true;
  } else {
    enableContentDraggable.value = false;
  }
};

const pullUpPanel = () => {
  panelHeight.value = maxPanelHeight.value;
  handleHeightChange({ height: maxPanelHeight.value });
};

export interface BottomSheetLayoutInstance {
  refreshScrollContainer: () => void;
  pullUpPanel: () => void;
}

const setPanelHeight = useThrottleFn((height: number) => {
  panelHeight.value = height;
}, 800);

defineExpose({
  refreshScrollContainer,
  pullUpPanel,
});
</script>
<template>
  <div>
    <div ref="headerRef">
      <slot name="header" />
    </div>
    <VanFloatingPanel
      class="bottom-sheet-panel border bg-base-100"
      :class="{
        'pb-[var(--bottom-nav-height)]': route.meta.showBottomNav,
      }"
      :style="{
        '--panel-bg-color': 'var(--bg-base-300)',
        '--van-floating-panel-background': 'var(--bg-base-100)',
      }"
      :anchors="[minPanelHeight, maxPanelHeight]"
      :content-draggable="enableContentDraggable"
      :height="panelHeight"
      @update:height="setPanelHeight"
      @height-change="handleHeightChange"
    >
      <template #header>
        <slot name="panel-header" />
      </template>
      <slot
        :scroller-ref="scrollerRef"
        :is-reach-max-height="isReachMaxHeight"
      />
    </VanFloatingPanel>
    <slot name="root" />
  </div>
</template>
